import { Component, DestroyRef, forwardRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IonList, IonSegment, IonSegmentButton } from '@ionic/angular';
import { tap } from 'rxjs';

import { TextareaComponent } from '@ninety/mobile/platform/components';
import { UserNamePipe } from '@ninety/mobile/platform/services';

import { DiscussionModel, ValueRating } from '../../../models/discussion.models';
import { DiscussionCoreValuesFormValue, DiscussionFormMode } from '../../../models/form/discussion-form.model';
import { DiscussionFormBase } from '../discussion-form-base/discussion-form-base';

@Component({
  selector: 'nty-core-values-form',
  imports: [ReactiveFormsModule, IonList, IonSegment, IonSegmentButton, UserNamePipe, TextareaComponent],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DiscussionCoreValuesFormComponent), multi: true },
  ],
  templateUrl: 'discussion-core-values-form.component.html',
  styleUrls: ['../../discussion-form-container/common.scss'],
})
export class DiscussionCoreValuesFormComponent extends DiscussionFormBase implements OnInit, ControlValueAccessor {
  private destroyRef = inject(DestroyRef);

  @Input({ required: true }) discussion!: DiscussionModel;
  @Input() mode: DiscussionFormMode = DiscussionFormMode.Meeting;

  ValueRating = ValueRating;

  form = new FormGroup({
    manager: new FormGroup({
      ratings: new FormArray<FormControl<ValueRating>>([], { validators: [Validators.required] }),
      notes: new FormControl<string>('', { nonNullable: true }),
    }),
    managee: new FormGroup({
      ratings: new FormArray<FormControl<ValueRating>>([], { validators: [Validators.required] }),
      notes: new FormControl<string>('', { nonNullable: true }),
    }),
  });

  ngOnInit() {
    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          if (this.onChange) this.onChange(this.form.getRawValue());
          if (this.onTouched) this.onTouched();
        })
      )
      .subscribe();
  }

  writeValue(val: DiscussionCoreValuesFormValue): void {
    this.form.reset();

    this.form.patchValue(val);

    val.manager.ratings.forEach(rating =>
      this.form.controls.manager.controls.ratings.push(
        new FormControl(rating, { nonNullable: true, validators: [Validators.required] })
      )
    );

    val.managee.ratings.forEach(rating =>
      this.form.controls.managee.controls.ratings.push(
        new FormControl(rating, { nonNullable: true, validators: [Validators.required] })
      )
    );
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }

    if (this.mode === DiscussionFormMode.Manager) {
      this.form.controls.managee.disable();
    }

    if (this.mode === DiscussionFormMode.Managee) {
      this.form.controls.manager.disable();
    }
  }

  get managerRatingsCtrls() {
    return this.form.controls.manager.controls.ratings.controls;
  }

  get manageeRatingsCtrls() {
    return this.form.controls.managee.controls.ratings.controls;
  }
}
